import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Asset } from '../../../../store/seedanceTypes';
import { useLibraryPageController } from '../../../library';
import { useSeedanceLibraryStore } from '../../../../store/seedanceLibraryStore';
import { useSeedanceWorkspaceStore } from '../../../../store/seedanceWorkspaceStore';
import { getLocalStorageValue, SEEDANCE_STORAGE_KEYS } from '../../../../utils/seedanceStorage';
import { AssetCard } from '../../../../components/AssetCard';
import { AudioAssetCard } from '../../../../components/AudioAssetCard';
import { VideoAssetCard } from '../../../../components/VideoAssetCard';
import { VirtualAvatarGroupSwitcher } from '../../../../components/VirtualAvatarGroupSwitcher';
import { LIBRARY_AUDIO_ACCEPT } from '../../../../utils/libraryAudioUpload';
import './libraryPicker.css';

export const LibraryPicker: React.FC = () => {
  const pickerScrollRef = useRef<HTMLDivElement | null>(null);
  const pickerLoadMoreSentinelRef = useRef<HTMLDivElement | null>(null);

  const hasTaskListConfig = useCallback(() => {
    const userId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId).trim();
    return Boolean(userId);
  }, []);

  const libPickerTarget = useSeedanceLibraryStore((s) => s.libPickerTarget);
  const clearLibPicker = useSeedanceLibraryStore((s) => s.clearLibPicker);
  const libPickerExternalConsumer = useSeedanceLibraryStore((s) => s.libPickerExternalConsumer);
  const libSelectedGroupId = useSeedanceLibraryStore((s) => s.libSelectedGroupId);

  // Picker-specific independent state
  const libPickerRemoteAssets = useSeedanceLibraryStore((s) => s.libPickerRemoteAssets);
  const libPickerPortraitHasMore = useSeedanceLibraryStore((s) => s.libPickerPortraitHasMore);
  const libPickerPortraitLoading = useSeedanceLibraryStore((s) => s.libPickerPortraitLoading);
  const libPickerPortraitLoadingMore = useSeedanceLibraryStore((s) => s.libPickerPortraitLoadingMore);
  const libPickerSearchName = useSeedanceLibraryStore((s) => s.libPickerSearchName);
  const setLibPickerSearchName = useSeedanceLibraryStore((s) => s.setLibPickerSearchName);
  const libUploading = useSeedanceLibraryStore((s) => s.libUploading);

  const nodes = useSeedanceWorkspaceStore((s) => s.nodes);

  const pickerOpen = Boolean(libPickerTarget);

  const [nameSearchInput, setNameSearchInput] = useState(libPickerSearchName);

  // Sync input when external clear happens
  useEffect(() => {
    if (!libPickerSearchName) setNameSearchInput('');
  }, [libPickerSearchName]);

  const submitNameSearch = useCallback(() => {
    setLibPickerSearchName(nameSearchInput.trim());
  }, [nameSearchInput, setLibPickerSearchName]);

  const clearNameSearch = useCallback(() => {
    setNameSearchInput('');
    setLibPickerSearchName('');
  }, [setLibPickerSearchName]);

  const handleClose = useCallback(() => {
    setLibPickerSearchName('');
    clearLibPicker();
  }, [clearLibPicker, setLibPickerSearchName]);

  const { applyLibAsset, uploadInputRef, uploadPortraitsToSelectedGroup } = useLibraryPageController({
    active: pickerOpen,
    hasTaskListConfig,
    pagingLayout: 'picker',
    scrollRootRef: pickerScrollRef,
    loadMoreSentinelRef: pickerLoadMoreSentinelRef,
    skipInitialLoadIfPopulated: true,
  });

  const workspaceNode = libPickerTarget ? nodes.find((n) => n.id === libPickerTarget) : undefined;
  const external =
    libPickerTarget && libPickerExternalConsumer?.nodeId === libPickerTarget
      ? libPickerExternalConsumer
      : null;

  const pickerMediaType = external?.mediaType ?? workspaceNode?.type ?? 'image';

  const pickerAssets = libPickerTarget
    ? libPickerRemoteAssets.filter((a) => {
        const t = external ? external.mediaType : workspaceNode?.type;
        return t ? a.type === t : false;
      })
    : [];

  const uploadAccept =
    pickerMediaType === 'video'
      ? 'video/*,.mp4,.mov,.webm'
      : pickerMediaType === 'image'
        ? 'image/*'
        : pickerMediaType === 'audio'
          ? LIBRARY_AUDIO_ACCEPT
          : 'image/*,video/*,.mp4,.mov,.webm';

  const pickerTypeLabel = libPickerTarget
    ? ({
        image: '图片',
        video: '视频',
        audio: '音频',
      } as const)[external?.mediaType ?? workspaceNode?.type ?? 'image']
    : '';

  const searchPrefix = libPickerSearchName.trim()
    ? `搜索「${libPickerSearchName.trim()}」：`
    : '';

  const loadStatusText = libPickerPortraitLoading
    ? '加载中…'
    : pickerAssets.length
      ? libPickerPortraitHasMore
        ? `${searchPrefix}已加载 ${pickerAssets.length} 个${pickerTypeLabel}，继续向下滚动加载更多`
        : `${searchPrefix}共 ${pickerAssets.length} 个${pickerTypeLabel}素材`
      : libPickerRemoteAssets.length && libPickerPortraitHasMore
        ? '继续向下滚动加载更多素材…'
        : '';

  const handlePickAsset = (asset: Asset) => {
    if (!libPickerTarget) return;
    if (external) {
      external.onApply(asset);
      clearLibPicker();
      return;
    }
    applyLibAsset(libPickerTarget, asset.id);
  };

  if (!libPickerTarget) return null;

  return (
    <div className="lib-picker-mask" onClick={handleClose}>
      <div className="lib-picker" onClick={(e) => e.stopPropagation()}>
        <div className="lp-head">
          <span>选择{pickerTypeLabel}素材</span>
          <button type="button" className="lp-close" onClick={handleClose}>
            ✕
          </button>
        </div>
        <div className="lp-group-bar">
          <VirtualAvatarGroupSwitcher active={pickerOpen} />
        </div>
        {libSelectedGroupId.trim() ? (
          <div className="lp-search">
            <div className="lib-name-search-field">
              <input
                className="lib-name-search-input"
                type="search"
                placeholder="按素材名称搜索…"
                value={nameSearchInput}
                onChange={(e) => setNameSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitNameSearch();
                }}
                aria-label="按素材名称搜索"
              />
              {nameSearchInput.trim() ? (
                <button
                  type="button"
                  className="lib-name-search-clear"
                  onClick={clearNameSearch}
                  aria-label="清除搜索"
                >
                  ✕
                </button>
              ) : null}
              <button type="button" className="lib-name-search-btn" onClick={submitNameSearch}>
                搜索
              </button>
            </div>
          </div>
        ) : null}
        <div className="lp-body" ref={pickerScrollRef}>
          {!libSelectedGroupId.trim() ? (
            <div className="lp-empty">请选择上方素材组，或先在「素材库」页配置系统访问令牌</div>
          ) : (
            <>
              {loadStatusText ? (
                <p className="lp-count" role="status" aria-live="polite">
                  {loadStatusText}
                </p>
              ) : null}
              <div className="lp-grid">
                <button
                  type="button"
                  className="lp-upload-btn"
                  disabled={libUploading}
                  onClick={() => uploadInputRef.current?.click()}
                  aria-label={libUploading ? '正在上传素材' : '上传素材到当前素材组'}
                >
                  <span className="lp-upload-ico" aria-hidden>
                    {libUploading ? '⏳' : '📤'}
                  </span>
                  <span>{libUploading ? '上传中…' : '上传素材'}</span>
                </button>
                <input
                  type="file"
                  multiple
                  accept={uploadAccept}
                  ref={uploadInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files) uploadPortraitsToSelectedGroup(e.target);
                  }}
                />
                {pickerAssets.map((asset) =>
                  asset.type === 'video' ? (
                    <VideoAssetCard
                      key={asset.id}
                      variant="mini"
                      videoUrl={asset.url}
                      poster={asset.thumb}
                      title={asset.name}
                      onClick={() => handlePickAsset(asset)}
                    />
                  ) : asset.type === 'audio' ? (
                    <AudioAssetCard
                      key={asset.id}
                      variant="mini"
                      audioUrl={asset.url}
                      title={asset.name}
                      onClick={() => handlePickAsset(asset)}
                    />
                  ) : (
                    <AssetCard
                      key={asset.id}
                      variant="mini"
                      src={asset.thumb || asset.url}
                      title={asset.name}
                      onClick={() => handlePickAsset(asset)}
                    />
                  ),
                )}
              </div>
              {libPickerPortraitLoading && pickerAssets.length === 0 ? (
                <p className="lp-empty-hint">正在加载素材…</p>
              ) : pickerAssets.length === 0 ? (
                <p className="lp-empty-hint">
                  {libPickerSearchName.trim()
                    ? `未找到名称含「${libPickerSearchName.trim()}」的${pickerTypeLabel}素材，可上传新素材`
                    : `当前素材组内暂无${pickerTypeLabel}素材，点击首张卡片上传`}
                </p>
              ) : null}
              {(pickerAssets.length > 0 || libPickerPortraitHasMore) && (
                <div
                  ref={pickerLoadMoreSentinelRef}
                  className="lp-load-more"
                  role="status"
                  aria-live="polite"
                >
                  {libPickerPortraitLoadingMore
                    ? '加载更多…'
                    : libPickerPortraitHasMore
                      ? pickerAssets.length
                        ? ''
                        : '向下滑动继续加载…'
                      : '已加载全部'}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
